import {
  DISCARDABLE_INBOUND_EVENT_STATUSES,
  RETRYABLE_INBOUND_EVENT_STATUSES,
  type EventQueueActivityEntry,
  type EventQueueAlertKind,
  type EventQueueBackgroundJobHealth,
  type EventQueueBackgroundJobSchedule,
  type EventQueueHookRejectionReason,
  type EventQueueRuleModeSource,
  type EventQueueWorkerLiveness,
  type EventQueueAttemptOutcome,
  type EventQueueBulkActionSummary,
  type EventQueueJobFact,
  type EventQueueShadowMismatch,
  type EventQueueShadowMismatchKind,
  type EventQueueHealth,
  type EventQueueShadowReasonCode,
  type EventQueueShadowRepo,
  type InboundEventStatus,
} from "@t3tools/contracts";

export type StatusBadgeVariant = "error" | "info" | "outline" | "secondary" | "success" | "warning";

const HEALTH_BADGES: Record<EventQueueHealth, { label: string; variant: StatusBadgeVariant }> = {
  active: { label: "Active", variant: "success" },
  backlogged: { label: "Backlogged", variant: "warning" },
  failing: { label: "Failing", variant: "error" },
  idle: { label: "Idle", variant: "secondary" },
  no_consumer: { label: "No consumer", variant: "error" },
  no_workers: { label: "No live workers", variant: "error" },
  paused: { label: "Paused", variant: "warning" },
};

export function healthBadge(health: EventQueueHealth): {
  label: string;
  variant: StatusBadgeVariant;
} {
  return HEALTH_BADGES[health];
}

/** `850ms`, `2.4s`, `52s`, `95s`, `3m`; `—` when there's nothing to measure. */
export function formatDurationMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 10_000) return `${(ms / 1_000).toFixed(1)}s`;
  // Seconds up to two minutes, so neighbouring chart ticks don't all read `1m`.
  if (ms < 120_000) return `${Math.round(ms / 1_000)}s`;
  return formatAgeSeconds(ms / 1_000);
}

const STATUS_VARIANTS: Record<InboundEventStatus, StatusBadgeVariant> = {
  discarded: "outline",
  failed: "error",
  ignored: "secondary",
  processing: "info",
  queued: "warning",
  succeeded: "success",
};

export function statusBadgeVariant(status: InboundEventStatus): StatusBadgeVariant {
  return STATUS_VARIANTS[status];
}

export function canRetryEvent(status: InboundEventStatus): boolean {
  return (RETRYABLE_INBOUND_EVENT_STATUSES as readonly InboundEventStatus[]).includes(status);
}

export function canDiscardEvent(status: InboundEventStatus): boolean {
  return (DISCARDABLE_INBOUND_EVENT_STATUSES as readonly InboundEventStatus[]).includes(status);
}

const GITHUB_PULL = /^github:([\w.-]+)\/([\w.-]+)#(\d+)$/i;

/** A subject a person can read, and the pull request it points at. */
export function formatJobSubject(subjectKey: string | null): {
  href: string | null;
  label: string;
} {
  const match = subjectKey ? GITHUB_PULL.exec(subjectKey) : null;
  if (match?.[2] && match[3] && match[1]) {
    return {
      href: `https://github.com/${match[1]}/${match[2]}/pull/${match[3]}`,
      label: `${match[2]} #${match[3]}`,
    };
  }
  return { href: null, label: subjectKey ?? "This job" };
}

/** A job's name: the pull request or subject it's about, or its event type when it has none. */
export function jobTitle(event: { eventType: string; subjectKey: string | null }): {
  href: string | null;
  label: string;
} {
  return event.subjectKey
    ? formatJobSubject(event.subjectKey)
    : { href: null, label: event.eventType };
}

/**
 * What this run did, in a few words. The queue header already says what the
 * job is, so a success does not repeat that sentence.
 */
export function describeJobOutcome(event: {
  attempts: number;
  decisionReason: string | null;
  errorMessage: string | null;
  queueName: string | null;
  status: InboundEventStatus;
}): string {
  if (event.status === "failed") {
    return event.errorMessage ? `Failed · ${event.errorMessage}` : "Failed";
  }
  if (event.status === "ignored") {
    return event.decisionReason
      ? `Ignored · ${event.decisionReason}`
      : "Ignored · no matching rule";
  }
  if (event.status === "discarded") return "Discarded";
  if (event.status === "processing") return "Running";
  if (event.status === "queued") return event.attempts > 0 ? "Retrying" : "Waiting";
  if (event.queueName === "github_pr_reconcile") return "Asked for review";
  return "Finished";
}

/** One sentence for the detail panel, from fields we already store. */
export function describeJobSummary(event: {
  attempts: number;
  decisionReason: string | null;
  errorMessage: string | null;
  queueName: string | null;
  ruleId: string | null;
  status: InboundEventStatus;
}): string {
  const outcome = describeJobOutcome(event);
  if (event.status === "ignored") return outcome;
  if (event.ruleId) return `${outcome}. Matched ${event.ruleId}.`;
  return outcome;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${BYTE_UNITS[exponent]}`;
}

/** Compact age such as `45s`, `12m`, `3h`, or `2d`; `—` when there is nothing to age. */
export function formatAgeSeconds(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h`;
  return `${Math.floor(seconds / 86_400)}d`;
}

/**
 * Milliseconds since the epoch, or NaN. The API sends Postgres's own timestamp
 * text (`2026-10-05 19:21:46.112979+00`), which only some engines parse, so it
 * is normalized to ISO 8601 first.
 */
export function parseTimestamp(value: string): number {
  const match =
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?$/i.exec(
      value.trim(),
    );
  if (!match) return Date.parse(value);
  const [, date, time, fraction, zone] = match;
  const millis = fraction ? `.${fraction.padEnd(3, "0").slice(0, 3)}` : "";
  const offset =
    !zone || zone.toUpperCase() === "Z"
      ? "Z"
      : zone.length === 3
        ? `${zone}:00`
        : zone.includes(":")
          ? zone
          : `${zone.slice(0, 3)}:${zone.slice(3)}`;
  return Date.parse(`${date}T${time}${millis}${offset}`);
}

export function formatRelativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "—";
  const at = parseTimestamp(iso);
  if (Number.isNaN(at)) return "—";
  const seconds = Math.max(0, (now - at) / 1_000);
  return seconds < 5 ? "just now" : `${formatAgeSeconds(seconds)} ago`;
}

export function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const at = parseTimestamp(iso);
  return Number.isNaN(at) ? "—" : new Date(at).toLocaleString();
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/** Whether every webhook got the work it asked for: no data yet, healthy, or how many are open. */
export function automationHealthBadge(
  repo: Pick<EventQueueShadowRepo, "unexplainedOpen" | "windowStart">,
): { label: string; variant: StatusBadgeVariant } {
  if (!repo.windowStart) return { label: "No data yet", variant: "secondary" };
  if (repo.unexplainedOpen > 0) {
    return { label: `${formatCount(repo.unexplainedOpen)} open`, variant: "error" };
  }
  return { label: "Healthy", variant: "success" };
}

const SHADOW_REASON_LABELS: Record<EventQueueShadowReasonCode, string> = {
  comment_superseded_within_poll: "Comment edited again before its pass ran",
  pr_shorter_than_poll: "PR closed before PR automation tracked it",
  review_on_ineligible_pr: "Review on a PR that PR automation doesn't track yet",
};

/** Plain-language explanation for a mismatch's reason code or hint, or null. */
export function describeShadowMismatch(mismatch: EventQueueShadowMismatch): string | null {
  if (mismatch.reasonCode) return SHADOW_REASON_LABELS[mismatch.reasonCode];
  if (mismatch.kind === "covered") {
    return "A webhook asked for this, but its pass didn't write it; the startup catch-up did";
  }
  if (mismatch.hint === "no_webhook_for_pr_in_window") {
    return "No webhook arrived for this PR around then, so the startup catch-up did it; check GitHub's Recent deliveries";
  }
  return null;
}

export const SHADOW_KIND_BADGES: Record<
  EventQueueShadowMismatchKind,
  { label: string; variant: StatusBadgeVariant }
> = {
  covered: { label: "Caught up", variant: "warning" },
  extra: { label: "Not done", variant: "warning" },
  missed: { label: "Missed", variant: "warning" },
};

const PREDICTOR_SKIP_LABELS: Record<string, string> = {
  bot_author: "Written by the Operators account",
  draft: "PR is a draft",
  ignored_action: "Action PR automation doesn't act on",
  malformed: "Payload missing fields",
  no_mention: "No @operators mention",
  not_pull_request: "Not a pull request",
  pending_review: "Pending review",
  untracked_pr: "PR not tracked yet",
  untracked_repo: "Repository not tracked",
};

/** Why the predictor expected no receipt for an event, in plain words. */
export function describePredictorSkip(skipped: string | null): string | null {
  if (!skipped) return null;
  return PREDICTOR_SKIP_LABELS[skipped] ?? skipped;
}

/** `92%`, or `—` before anything has been judged. Rounds down so 99.9% never shows as 100%. */
export function formatMatchRatio(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return "—";
  return `${Math.floor(ratio * 1000) / 10}%`;
}

/** Signed delay from the webhook to the receipt: `0.8s`, `52s`, or negative when a pass beat it. */
export function formatLeadMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "—";
  const seconds = ms / 1_000;
  return Math.abs(seconds) < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`;
}

/** Bulk retries are released 5 a second, so the last of `count` starts this many seconds in. */
export const BULK_RETRY_PER_SECOND = 5;

export function bulkRetrySpreadSeconds(count: number): number {
  return count <= 0 ? 0 : Math.floor((count - 1) / BULK_RETRY_PER_SECOND);
}

const plural = (count: number, word: string) =>
  `${formatCount(count)} ${word}${count === 1 ? "" : "s"}`;

/**
 * The confirmation for a bulk action, naming what it acts on (Oban's
 * "Cancel 42 retryable jobs in default?"): count, queue and error.
 */
export function describeBulkConfirmation(input: {
  action: "discard" | "retry";
  count: number;
  displayName?: string;
  message: string;
  queueName: string;
}): string {
  const name = input.displayName ?? input.queueName;
  const jobs = plural(input.count, "job");
  if (input.action === "discard") {
    return `Discard ${jobs} in ${name} that failed with ${input.message}?`;
  }
  const spread = bulkRetrySpreadSeconds(input.count);
  const pacing =
    spread === 0
      ? "They start over right away."
      : `They start over about ${plural(spread, "second")}, ${BULK_RETRY_PER_SECOND} a second.`;
  return `Retry ${jobs} in ${name} that failed with ${input.message}? ${pacing}`;
}

const OUTCOME_PHRASES: Partial<Record<InboundEventStatus, string>> = {
  discarded: "discarded",
  failed: "failed again",
  processing: "running",
  queued: "waiting",
  succeeded: "succeeded",
};
const OUTCOME_ORDER: InboundEventStatus[] = [
  "succeeded",
  "failed",
  "processing",
  "queued",
  "discarded",
  "ignored",
];

/** `retried 42: 40 succeeded, 2 failed again`, from a bulk action and where its jobs are now. */
export function describeBulkOutcome(summary: EventQueueBulkActionSummary): string {
  const verb = summary.action === "event_queue.bulk_discard" ? "discarded" : "retried";
  const parts = OUTCOME_ORDER.flatMap((status) => {
    const count = summary.outcome.now[status] ?? 0;
    if (count === 0) return [];
    const phrase =
      verb === "discarded" && status === "discarded"
        ? "still discarded"
        : (OUTCOME_PHRASES[status] ?? status);
    return [`${formatCount(count)} ${phrase}`];
  });
  const gone =
    summary.outcome.acted -
    OUTCOME_ORDER.reduce((total, status) => total + (summary.outcome.now[status] ?? 0), 0);
  if (gone > 0) parts.push(`${formatCount(gone)} removed by retention`);
  return `${verb} ${formatCount(summary.outcome.acted)}${parts.length > 0 ? `: ${parts.join(", ")}` : ""}`;
}

const FACT_VARIANTS: Record<EventQueueJobFact["tone"], StatusBadgeVariant> = {
  danger: "error",
  info: "info",
  warning: "warning",
};

export function factBadgeVariant(fact: EventQueueJobFact): StatusBadgeVariant {
  return FACT_VARIANTS[fact.tone];
}

export const ATTEMPT_OUTCOME_BADGES: Record<
  EventQueueAttemptOutcome,
  { label: string; variant: StatusBadgeVariant }
> = {
  dead_lettered: { label: "Dead-lettered", variant: "error" },
  failed: { label: "Failed", variant: "error" },
  lease_lost: { label: "Stalled", variant: "warning" },
  retrying: { label: "Failed, retrying", variant: "warning" },
  running: { label: "Running", variant: "info" },
  succeeded: { label: "Succeeded", variant: "success" },
};

/** `v1-abc123` from `registry.fly.io/operators-control-plane:v1-abc123`; the tag is what tells deploys apart. Local runs and jobs from before attempts were recorded have no image. */
export function shortImageRef(imageRef: string | null): string {
  if (!imageRef) return "Unknown version";
  const tag = imageRef.slice(imageRef.lastIndexOf(":") + 1);
  return tag.length > 0 && tag !== imageRef ? tag : imageRef.slice(-24);
}

/**
 * Consecutive attempts that ended the same way with the same error, as one
 * run, so eight identical 502s read as one line instead of eight.
 */
export function groupAttempts<T extends { errorMessage: string | null; outcome: string }>(
  attempts: readonly T[],
): T[][] {
  const runs: T[][] = [];
  for (const attempt of attempts) {
    const run = runs.at(-1);
    const last = run?.at(-1);
    if (
      run &&
      last &&
      last.outcome === attempt.outcome &&
      last.errorMessage === attempt.errorMessage
    ) {
      run.push(attempt);
    } else {
      runs.push([attempt]);
    }
  }
  return runs;
}

/**
 * Which deploys a failure group happened on, so "did my deploy do this?" has an
 * answer: one version, a few, or nothing when no attempt recorded its image.
 */
export function describeFailureVersions(
  versions: readonly { count: number; imageRef: string | null }[],
): string | null {
  const known = versions.filter((version) => version.imageRef !== null);
  if (known.length === 0) return null;
  if (known.length === 1) return `all on ${shortImageRef(known[0]!.imageRef)}`;
  const [top] = [...known].sort((left, right) => right.count - left.count);
  return `on ${known.length} versions, mostly ${shortImageRef(top!.imageRef)}`;
}

/** When per-attempt history began; older jobs have none, and the sheet says so. */
export const ATTEMPT_HISTORY_STARTED = "Oct 6";

const ACTIVITY_LABELS: Record<string, string> = {
  "event_queue.bulk_discard": "Discarded jobs in bulk",
  "event_queue.acknowledge": "Acknowledged an Inbox item",
  "event_queue.bulk_retry": "Retried jobs in bulk",
  "event_queue.concurrency": "Changed how many run at once",
  "event_queue.discard": "Discarded a job",
  "event_queue.pause": "Paused a queue",
  "event_queue.resume": "Resumed a queue",
  "event_queue.retry": "Retried a job",
  "event_queue.shadow_acknowledge": "Acknowledged a GitHub mismatch",
  "event_queue.unacknowledge": "Brought back an Inbox item",
};

const stringField = (value: Record<string, unknown> | null, key: string): string | null => {
  const field = value?.[key];
  return typeof field === "string" && field.length > 0 ? field : null;
};

const arrayLength = (value: Record<string, unknown> | null, key: string): number => {
  const field = value?.[key];
  return Array.isArray(field) ? field.length : 0;
};

/** A readable title and one-line summary for an audit entry; unknown actions fall back to their name. */
export function describeActivity(entry: EventQueueActivityEntry): {
  detail: string | null;
  queueName: string | null;
  title: string;
} {
  const title = ACTIVITY_LABELS[entry.action] ?? entry.action;
  const queueName =
    stringField(entry.after, "queueName") ??
    stringField(entry.before, "queueName") ??
    (entry.targetType === "event_queue" ? entry.targetId : null) ??
    (typeof entry.after?.filter === "object" && entry.after.filter !== null
      ? stringField(entry.after.filter as Record<string, unknown>, "queue")
      : null);
  if (entry.action === "event_queue.bulk_retry" || entry.action === "event_queue.bulk_discard") {
    const acted = arrayLength(entry.after, "actedIds");
    const skipped = arrayLength(entry.after, "skipped");
    const name = stringField(entry.after, "name");
    const error = stringField(entry.after, "error");
    return {
      detail: [
        `${formatCount(acted)} acted on`,
        skipped > 0 ? `${formatCount(skipped)} skipped` : null,
        error ? `stopped early: ${error}` : null,
        name ? `\u201c${name}\u201d` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      queueName,
      title,
    };
  }
  if (entry.action === "event_queue.acknowledge") {
    const expiresAt = stringField(entry.after, "expiresAt");
    return {
      detail: [
        stringField(entry.after, "note") ? `\u201c${stringField(entry.after, "note")}\u201d` : null,
        expiresAt ? `until ${formatTimestamp(expiresAt)}` : "until it fails again",
      ]
        .filter(Boolean)
        .join(" · "),
      queueName,
      title,
    };
  }
  if (entry.action === "event_queue.concurrency") {
    const before = entry.before?.desiredConcurrency;
    const after = entry.after?.desiredConcurrency;
    return {
      detail: `${typeof before === "number" ? before : "default"} \u2192 ${typeof after === "number" ? after : "default"} at a time`,
      queueName,
      title,
    };
  }
  if (entry.action === "event_queue.pause") {
    return { detail: stringField(entry.after, "reason"), queueName, title };
  }
  if (entry.action === "event_queue.shadow_acknowledge") {
    return { detail: stringField(entry.after, "reason"), queueName: null, title };
  }
  if (entry.action === "event_queue.retry" || entry.action === "event_queue.discard") {
    const from = stringField(entry.before, "status");
    const to = stringField(entry.after, "status");
    return { detail: from && to ? `${from} \u2192 ${to}` : null, queueName, title };
  }
  return { detail: null, queueName, title };
}

/** `4 live · 4 per Machine`: what's running now beside what the handler asks for. */
export function describeWorkerCount(input: {
  concurrency: number | null;
  workersLive: number | null;
}): string {
  if (input.concurrency === null) return "—";
  const configured = `${input.concurrency} per Machine`;
  return input.workersLive === null
    ? configured
    : `${formatCount(input.workersLive)} live · ${configured}`;
}

export const WORKER_LIVENESS_BADGES: Record<
  EventQueueWorkerLiveness,
  { label: string; variant: StatusBadgeVariant }
> = {
  late: { label: "Late", variant: "warning" },
  live: { label: "Live", variant: "success" },
  offline: { label: "Offline", variant: "error" },
};

export const ALERT_KIND_LABELS: Record<EventQueueAlertKind, string> = {
  backlog_age: "Backlog is old",
  missing_workers: "No live workers",
  new_error_group: "New error",
};

/** `12%`, or `—` without live workers to measure. */
export function formatUtilization(share: number | null): string {
  if (share === null || !Number.isFinite(share)) return "—";
  return `${Math.round(Math.min(1, Math.max(0, share)) * 100)}%`;
}

/** Plain words for why a delivery was turned away, and what to check. */
export const HOOK_REJECTION_REASONS: Record<
  EventQueueHookRejectionReason,
  { hint: string | null; label: string }
> = {
  duplicate: { hint: "The sender redelivered something already stored.", label: "Duplicate" },
  internal_error: {
    hint: "The route threw; it's in Sentry under workflow hooks.",
    label: "Internal error",
  },
  invalid_signature: {
    hint: "A webhook secret may have rotated; check Doppler and the sender's settings.",
    label: "Bad signature",
  },
  malformed: { hint: "Required headers or fields were missing.", label: "Malformed" },
  missing_timestamp: {
    hint: "Linear signs a timestamp; this one had none.",
    label: "Missing timestamp",
  },
  not_configured: {
    hint: "The source has no webhook secret set here, so it's answered like an unknown route.",
    label: "Source not set up",
  },
  payload_too_large: { hint: "Over the 1 MB cap.", label: "Too large" },
  stale_timestamp: {
    hint: "Signed too long ago or too far ahead; check clocks or replays.",
    label: "Stale timestamp",
  },
  unavailable: {
    hint: "The database was unreachable; the sender should retry.",
    label: "Database down",
  },
  unknown_route: {
    hint: "Anything posting to a path that isn't served, often scanners.",
    label: "Unknown route",
  },
};

export const RULE_MODE_SOURCE_LABELS: Record<EventQueueRuleModeSource, string> = {
  default: "Default",
  rule_override: "Rule override",
  scope_override: "Scope override",
};

export const BACKGROUND_JOB_HEALTH_BADGES: Record<
  EventQueueBackgroundJobHealth,
  { label: string; variant: StatusBadgeVariant }
> = {
  disabled: { label: "Off", variant: "secondary" },
  failing: { label: "Failing", variant: "error" },
  late: { label: "Late", variant: "warning" },
  never_run: { label: "No run yet", variant: "outline" },
  ok: { label: "OK", variant: "success" },
};

const pad2 = (value: string) => value.padStart(2, "0");

/** The common cron shapes in words; anything else stays as the expression. */
function describeCron(cron: string): string {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return cron;
  const [minute, hour, day, month, weekday] = fields as [string, string, string, string, string];
  const everyDay = day === "*" && month === "*" && weekday === "*";
  if (!everyDay) return cron;
  if (minute === "*" && hour === "*") return "Every minute";
  const step = /^\*\/(\d+)$/.exec(minute);
  if (step && hour === "*") return `Every ${step[1]} minutes`;
  if (/^\d+$/.test(minute) && hour === "*") return `Hourly at :${pad2(minute)}`;
  if (/^\d+$/.test(minute) && /^\d+$/.test(hour)) return `Daily at ${pad2(hour)}:${pad2(minute)}`;
  return cron;
}

/** `Every 5s`, `Daily at 09:45 ET`; the cron expression when it doesn't read as words. */
export function describeJobSchedule(schedule: EventQueueBackgroundJobSchedule): string {
  if (schedule.kind === "interval") {
    return `Every ${formatDurationMs(schedule.intervalMs).replace(/\.0(?=s$)/, "")}`;
  }
  const words = describeCron(schedule.cron);
  if (!schedule.timeZone || schedule.timeZone === "UTC") return words;
  const zone = schedule.timeZone === "America/New_York" ? "ET" : schedule.timeZone;
  return `${words} ${zone}`;
}

export type FailureExplanation = { action: string; cause: string };

/** Known failures in plain words, first match wins; anything else stays as its raw error. */
const FAILURE_EXPLANATIONS: {
  explain: (match: RegExpMatchArray) => FailureExplanation;
  pattern: RegExp;
}[] = [
  {
    explain: () => ({
      action: "Discard it.",
      cause: "Someone triggered this failure on purpose to test the failure path.",
    }),
    pattern: /Diagnostics failure requested/i,
  },
  {
    explain: () => ({
      action:
        "Run the snapshot cleanup now to delete images nothing uses, then run this again. If it's still full, old images need deleting in Daytona.",
      cause:
        "Daytona's snapshot limit is full, so a fresh sandbox image can't be built. New sessions keep starting from the last good one.",
    }),
    pattern: /snapshot quota exceeded/i,
  },
  {
    explain: (match) => ({
      action:
        "A Slack workspace admin adds the missing permission to the Operators Slack app and reinstalls it, then updates SLACK_BOT_TOKEN in Doppler if the token changed. Then run it again.",
      cause: `The Operators Slack app isn't allowed to call ${match[1] ?? "that Slack method"}.`,
    }),
    pattern: /Slack ([\w.]+) failed: missing_scope/i,
  },
  {
    explain: () => ({
      action:
        "A Slack workspace admin adds the missing permission to the Operators Slack app and reinstalls it. Then run it again.",
      cause: "The Operators Slack app is missing a permission it needs.",
    }),
    pattern: /missing_scope/i,
  },
  {
    explain: () => ({
      action: "Invite the Operators app to the channel in Slack, then run it again.",
      cause: "The Operators Slack app isn't in the channel it posts to.",
    }),
    pattern: /not_in_channel|channel_not_found/i,
  },
  {
    explain: () => ({
      action:
        "Run it again; a timeout under load often passes the second time. If it keeps timing out, the query needs to get faster.",
      cause: "A database query ran past its time limit.",
    }),
    pattern: /statement timeout|canceling statement/i,
  },
  {
    explain: () => ({
      action:
        "Run it again; slow moments pass. If it keeps timing out, that call or query needs to get faster or get more time.",
      cause: "A call took longer than its time limit.",
    }),
    pattern: /timed out|timeout/i,
  },
  {
    explain: () => ({
      action: "Check the credential in Doppler and whether it was rotated or revoked.",
      cause: "A service rejected the credential Operators used.",
    }),
    pattern: /\b401\b|invalid_auth|token_revoked|not_authed|bad credentials|unauthorized/i,
  },
  {
    explain: () => ({
      action:
        "Wait a few minutes and retry. If it keeps happening, turn the queue down to run fewer at once.",
      cause: "A service is rate-limiting Operators.",
    }),
    pattern: /\b429\b|rate.?limit/i,
  },
  {
    explain: () => ({
      action: "Retry once the service is healthy; these are usually brief.",
      cause: "Another service had a temporary error.",
    }),
    pattern: /\b(502|503|504)\b|bad gateway|service unavailable|gateway time-?out/i,
  },
  {
    explain: () => ({
      action: "Retry; if it persists, check whether that service is up.",
      cause: "Operators couldn't reach another service.",
    }),
    pattern: /ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|socket hang up|fetch failed/i,
  },
];

/** A likely cause and next step for an error a person sees, or null when it isn't a known one. */
export function explainFailure(message: string | null): FailureExplanation | null {
  if (!message) return null;
  for (const { explain, pattern } of FAILURE_EXPLANATIONS) {
    const match = message.match(pattern);
    if (match) return explain(match);
  }
  return null;
}

/** "its last run", or "its last 3 runs", for a job failing in a row. */
export function describeFailureStreak(consecutiveFailures: number): string {
  return consecutiveFailures <= 1
    ? "its last run"
    : `its last ${formatCount(consecutiveFailures)} runs`;
}
